/**
 * Card → Google Drive folder map for the "See Files" links.
 *
 * Each card/entity that has a populated admin/accounting (or fund-docs) folder in
 * the user's Drive is mapped here. The "See Files" button just opens the folder in
 * a new tab — the user is already logged into Google Drive, so no in-app auth.
 *
 * Folder IDs verified live against the user's Drive on 28-Jun-2026.
 * Re-verify with the Drive connector if a folder is moved/renamed.
 *
 * Keyed by BOTH the canonical card name(s) AND the entity slug (used by EntityPopup),
 * so callers can resolve by whichever they have on hand.
 */

const FOLDER_BASE = 'https://drive.google.com/drive/folders/';

// id → friendly description (for reference only)
const FOLDER_IDS = {
  brutus:       '1r4n4avH-BM5AMs2zpDRmPVWXDvQy3-sv', // Venootschappen/Brutus & Co BV
  grykrica:     '1EuAsH2uyS_x4sDBXW5a47ic81ippgpzD', // Venootschappen/GryKriCa
  kxInvest:     '1293_9sqJ1ogmYIfSCkkWuzAH7fZu80P7', // Venootschappen/KX Invest
  marcJunior:   '1RMWOTqwRbtoR7RAMg62mkPgU2mjbcgVt', // Venootschappen/Marc Junior NV
  fom:          '1wQ9n5f48PKG-uMGf6hqF0rlQMNCXEjgj', // Investeringen/Force Over Mass
  everBlue:     '13nKYmPHWGjCJ7EBMIA4OHdsyTbzWRBvv', // Investeringen/EverBlue
  patrimonia:   '1FlPZ5XP0TfO3VvBVNiVrOApSjKDJHAjx', // Investeringen/Patrimonia
  gsRsu:        '1S-bVywNwIG-mWp8ngQH6bRn4IsefAWv-', // Investeringen/GS RSU
  gsStonebridge:'1lshfvuyjD-3c08cCxAkM2EB2Kg8LZ-Hx', // Investeringen/GS Stonebridge
  monarch:      '1N1tZnrypxdqBSrP4nL9AVGPNuO315pIq', // Investeringen/Monarch Assurance
};

// Canonical card name / entity slug → folder id key.
// Includes all the name variants used across InvestmentList's click router.
const NAME_TO_FOLDER = {
  // Brutus
  'Brutus':            'brutus',
  'Brutus & Co BV':    'brutus',
  'brutus-co-bv':      'brutus',
  // GryKriCa
  'GryKRYCA':          'grykrica',
  'GryKriCa':          'grykrica',
  'GryKriCa Comm.V':   'grykrica',
  'grykrica':          'grykrica',
  // Kx Invest
  'Kx Invest':         'kxInvest',
  'KX Invest':         'kxInvest',
  'KX Invest BV':      'kxInvest',
  'kx-invest':         'kxInvest',
  // Marc Junior
  'Marc Junior':       'marcJunior',
  'Marc Junior NV':    'marcJunior',
  // PE funds
  'FOM':               'fom',
  'EverBlue':          'everBlue',
  'GS StoneBridge':    'gsStonebridge',
  // Patrimonia
  'Patrimonia':        'patrimonia',
  // GS RSU
  'Fidelity GS':       'gsRsu',
  // Monarch
  'Monarch Assurance': 'monarch',
};

/**
 * Resolve a Drive folder URL for a card name or entity slug.
 * Returns null if there is no mapped folder (button should render nothing).
 */
export function getDriveFolderUrl(nameOrSlug) {
  if (!nameOrSlug) return null;
  const key = NAME_TO_FOLDER[nameOrSlug];
  const id = key && FOLDER_IDS[key];
  return id ? `${FOLDER_BASE}${id}` : null;
}

export default getDriveFolderUrl;
